import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import { PaymentMethodStatisticsItem } from '../../types/statistics';

interface PaymentMethodsDonutChartProps {
  paymentMethodsData: PaymentMethodStatisticsItem[];
}

const PaymentMethodsDonutChart: React.FC<PaymentMethodsDonutChartProps> = ({
  paymentMethodsData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate total count (amount) of all payment methods
  const totalCount = paymentMethodsData.reduce((sum, item) => sum + item.count, 0);

  const chartData = paymentMethodsData.map((item, index) => {
    const percentage = totalCount > 0 ? (item.count / totalCount) * 100 : 0;
    const roundedPercentage = Math.round(percentage * 10) / 10; // Round to 1 decimal place
    const methodLabel = t(`statistics.${item.method}`);
    return {
      id: index,
      value: item.count,
      label: `${methodLabel}: ${item.count} (${roundedPercentage}%)`,
      methodLabel,
      count: item.count,
      percentage: roundedPercentage,
    };
  });

  if (chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('statistics.paymentMethodsStatistics')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="body1" color="text.secondary">
              {t('statistics.noData')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const chartHeight = isMobile ? 300 : 400;
  const innerRadius = isMobile ? 40 : 60;
  const outerRadius = isMobile ? 80 : 120;
  const chartMargin = isMobile 
    ? { top: 20, right: 10, bottom: 20, left: 10 }
    : { top: 20, right: 20, bottom: 20, left: 20 };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('statistics.paymentMethodsStatistics')}
        </Typography>
        <Box sx={{ width: '100%', height: isMobile ? 'auto' : chartHeight, display: 'flex', justifyContent: 'center' }}>
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius,
                outerRadius,
                paddingAngle: 2,
                cornerRadius: 5,
                arcLabel: () => '', // No labels on the donut, use legend and tooltip instead
                arcLabelMinAngle: 10,
                valueFormatter: () => '',
              },
            ]}
            height={chartHeight}
            margin={chartMargin}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsDonutChart;

